import type { ManifestAsset } from "./types.js";

/**
 * Build a tree from asset albumPaths. Each node tracks the count of assets
 * in its entire subtree so the UI can show "Reusable Respirators (67)".
 */
export interface TreeNode {
  /** Display name for this segment (last segment of its path). */
  name: string;
  /** Full slash-joined path from root. */
  path: string;
  /** Direct children. */
  children: TreeNode[];
  /** Assets whose albumPath is exactly this node. */
  directAssets: number;
  /** Assets in this node + all descendants. */
  totalAssets: number;
}

/**
 * @param protectPath When the UI re-roots the tree at a folder (locked-folder
 * `?only=` mode, share viewer), that node and its ancestors must survive
 * passthrough-collapse — otherwise findNode(protectPath) comes up empty and the
 * re-root silently falls back to the full tree.
 */
/** Every album path an asset appears under (Canto multi-homing) — albumPaths[]
 *  when present, else the single albumPath, else none. */
export function assetAlbumPaths(asset: ManifestAsset): string[] {
  if (asset.albumPaths && asset.albumPaths.length) return asset.albumPaths;
  return asset.albumPath ? [asset.albumPath] : [];
}

export function buildFolderTree(assets: ManifestAsset[], protectPath = ""): TreeNode {
  const root: TreeNode = { name: "", path: "", children: [], directAssets: 0, totalAssets: 0 };
  const nodeByPath = new Map<string, TreeNode>();
  nodeByPath.set("", root);

  for (const asset of assets) {
    const paths = assetAlbumPaths(asset);
    root.totalAssets++; // count each asset ONCE in the "All assets" total
    if (paths.length === 0) {
      root.directAssets++;
      continue;
    }
    // A multi-homed asset is counted under EACH folder it belongs to — matching
    // how Canto's per-album counts overlap.
    for (const path of paths) {
      const segments = path.split("/").filter(Boolean);
      let parent = root;
      let currentPath = "";
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]!;
        currentPath = currentPath ? `${currentPath}/${seg}` : seg;
        let node = nodeByPath.get(currentPath);
        if (!node) {
          node = { name: seg, path: currentPath, children: [], directAssets: 0, totalAssets: 0 };
          nodeByPath.set(currentPath, node);
          parent.children.push(node);
        }
        if (i === segments.length - 1) node.directAssets++;
        node.totalAssets++;
        parent = node;
      }
    }
  }

  // Sort children alphabetically at every level (nice for Canto-style browsing)
  const sortRec = (node: TreeNode) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    node.children.forEach(sortRec);
  };
  sortRec(root);

  // Collapse "passthrough" folders: when a folder has no direct items and
  // exactly one child folder, fold the child's contents into the parent.
  // Cleans up redundant Canto patterns like "0075 / 0075 / <items>" where
  // the inner folder is just a duplicate of the SKU. Path stays unchanged so
  // assetMatchesPath still resolves the assets correctly via startsWith.
  collapseSingleChildChains(root, protectPath);
  return root;
}

function collapseSingleChildChains(node: TreeNode, protectPath: string): void {
  // Recurse first so we collapse leaves up.
  for (const c of node.children) collapseSingleChildChains(c, protectPath);
  // Only collapse for non-root passthrough folders.
  if (node.path && node.directAssets === 0 && node.children.length === 1) {
    const child = node.children[0]!;
    // Collapsing folds the CHILD into this node, deleting child.path from the
    // tree. Never delete the protected node or any of its ancestors.
    if (protectPath && (protectPath === child.path || protectPath.startsWith(`${child.path}/`))) {
      return;
    }
    if (node.name !== child.name) node.name = `${node.name} / ${child.name}`;
    node.directAssets = child.directAssets;
    node.children = child.children;
    // totalAssets is already correct (subtree count is invariant under collapse).
  }
}

/** Does this asset fall under the given path prefix (or its subtree) in ANY of
 *  its album memberships? */
export function assetMatchesPath(asset: ManifestAsset, pathPrefix: string): boolean {
  if (!pathPrefix) return true;
  return assetAlbumPaths(asset).some((p) => p === pathPrefix || p.startsWith(`${pathPrefix}/`));
}
